/**
 * Safe Storage Utility
 * Provides safe access to browser storage APIs with SSR compatibility
 */

class SafeStorage {
  // Session Storage methods
  static getSessionItem(key) {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        console.warn('Failed to get sessionStorage item:', key, error);
        return null;
      }
    }
    return null;
  }

  static setSessionItem(key, value) {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        console.warn('Failed to set sessionStorage item:', key, error);
      }
    }
  }

  static removeSessionItem(key) {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn('Failed to remove sessionStorage item:', key, error);
      }
    }
  }

  static clearSession() {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.clear();
      } catch (error) {
        console.warn('Failed to clear sessionStorage:', error);
      }
    }
  }

  // Local Storage methods (in case needed)
  static getLocalItem(key) {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        console.warn('Failed to get localStorage item:', key, error);
        return null;
      }
    }
    return null;
  }

  static setLocalItem(key, value) {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.warn('Failed to set localStorage item:', key, error);
      }
    }
  }

  static removeLocalItem(key) {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn('Failed to remove localStorage item:', key, error);
      }
    }
  }

  static clearLocal() {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.clear();
      } catch (error) {
        console.warn('Failed to clear localStorage:', error);
      }
    }
  }

  // Check if storage is available
  static isSessionStorageAvailable() {
    return typeof window !== 'undefined' && window.sessionStorage;
  }

  static isLocalStorageAvailable() {
    return typeof window !== 'undefined' && window.localStorage;
  }

  // JSON helper methods
  static getSessionJSON(key) {
    const item = this.getSessionItem(key);
    if (item) {
      try {
        return JSON.parse(item);
      } catch (error) {
        console.warn('Failed to parse sessionStorage JSON:', key, error);
        return null;
      }
    }
    return null;
  }

  static setSessionJSON(key, value) {
    try {
      this.setSessionItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn('Failed to stringify sessionStorage JSON:', key, error);
    }
  }

  static getLocalJSON(key) {
    const item = this.getLocalItem(key);
    if (item) {
      try {
        return JSON.parse(item);
      } catch (error) {
        console.warn('Failed to parse localStorage JSON:', key, error);
        return null;
      }
    }
    return null;
  }

  static setLocalJSON(key, value) {
    try {
      this.setLocalItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn('Failed to stringify localStorage JSON:', key, error);
    }
  }
}

export default SafeStorage;
